import { createContext, useContext, useMemo, type ReactNode } from "react";
import pageLocales from "@/data/page-locales.json";

export const LOCALES = ["it", "en", "de", "hr"] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "it";
export const LOCALE_LABELS: Record<Locale, string> = {
  it: "Italiano",
  en: "English",
  de: "Deutsch",
  hr: "Hrvatski",
};

export function isLocale(value: string | undefined | null): value is Locale {
  return !!value && (LOCALES as readonly string[]).includes(value);
}

export function localeFromPath(pathname: string): Locale {
  return isLocale(pathname.split("/").filter(Boolean)[0]) ? pathname.split("/").filter(Boolean)[0] as Locale : DEFAULT_LOCALE;
}

export function stripLocalePrefix(pathname: string): string {
  const parts = pathname.split("/").filter(Boolean);
  return isLocale(parts[0]) ? `/${parts.slice(1).join("/")}`.replace(/\/$/, "") || "/" : pathname || "/";
}

export function localePath(pathname: string, locale: Locale): string {
  const path = stripLocalePrefix(pathname);
  return locale === DEFAULT_LOCALE ? path : `/${locale}${path === "/" ? "" : path}`;
}

export function availableLocalesForPath(pathname: string): readonly Locale[] {
  const path = stripLocalePrefix(pathname.split(/[?#]/)[0]);
  const available = pageLocales[path as keyof typeof pageLocales];
  return available ? LOCALES.filter(locale => available.includes(locale)) : LOCALES;
}

export function localeAlternates(pathname: string): Partial<Record<Locale, string>> {
  return Object.fromEntries(availableLocalesForPath(pathname).map((locale) => [locale, localePath(pathname, locale)]));
}

export interface LocaleContextValue {
  locale: Locale;
  locales: readonly Locale[];
  localePath: (pathname: string, locale?: Locale) => string;
  alternates: (pathname: string) => Partial<Record<Locale, string>>;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  const value = useMemo<LocaleContextValue>(() => ({
    locale,
    locales: LOCALES,
    // Wouter already mounts non-default locales at /en, /de or /hr.
    // Internal Link href values must stay relative to that router base,
    // otherwise Wouter prepends the locale a second time (/en/en/...).
    localePath: (pathname) => stripLocalePrefix(pathname),
    alternates: localeAlternates,
  }), [locale]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error("useLocale must be used inside LocaleProvider");
  return value;
}